/**
 * Landing-URL marketing-param capture. The captured string feeds the startup
 * application's `url_params` attribution field, and `utm_partner` pre-fills
 * (and hides) the startup form's incubator/VC dropdown. Module-level memory:
 * the capture survives step navigation and page-internal remounts, but a full
 * reload with fresh marketing params re-captures (last-touch).
 */

// Query params the funnel carries in the URL across wizard steps. A re-entry
// carrying only these is not a fresh marketing landing and must not overwrite
// params captured at landing.
const STICKY_SIGNUP_PARAM_KEYS = new Set(['signupType', 'signupFlow', 'promoCode', 'content']);

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

// Marketing's dashboards key on this exact Title_Snake casing.
const UTM_PARAM_NAMES = {
  utm_source: 'UTM_Source',
  utm_medium: 'UTM_Medium',
  utm_campaign: 'UTM_Campaign',
  utm_term: 'UTM_Term',
  utm_content: 'UTM_Content',
} satisfies Record<(typeof UTM_PARAMS)[number], string>;

// The landing capture holds every query param, including the personal ones
// `/signup` accepts (email/first/last). Only these reach analytics.
const ATTRIBUTION_PARAM_KEYS = new Set<string>([...UTM_PARAMS, 'utm_partner', 'gclid', '_gl', '_gcl_au', '_gcl_aw']);

let landingUrlParams: string | null = null;
let utmPartner: string | null = null;

/**
 * ⚠️ Feed this raw query-string pairs —
 * `Object.fromEntries(new URLSearchParams(window.location.search))` — never
 * TanStack Router's parsed search object. The router's default `parseSearch`
 * runs every value through `JSON.parse` (see `signup-search.ts`), which is
 * lossy for attribution: `?utm_campaign=00123` becomes the number `123`, and
 * numeric-valued marketing params stop being strings. The string-only
 * signature makes the parsed-search object a compile error instead of a
 * silent drop.
 */
export function captureLandingUrlParams(queryParams: Record<string, string>): void {
  const params = new URLSearchParams();
  let hasMarketingParams = false;
  for (const [key, value] of Object.entries(queryParams)) {
    if (typeof value === 'string' && value !== '') {
      params.set(key, value);
      if (!STICKY_SIGNUP_PARAM_KEYS.has(key)) {
        hasMarketingParams = true;
      }
    }
  }

  // A re-entry carrying only sticky params must not clobber the marketing
  // params from the original landing. A re-entry with fresh marketing params
  // still overwrites (last-touch).
  if (!hasMarketingParams && landingUrlParams != null) {
    return;
  }

  const serialized = params.toString();
  landingUrlParams = serialized === '' ? '' : `?${serialized}`;

  // Set when present, clear when absent so a new landing without it doesn't
  // inherit a stale value.
  const partner = queryParams.utm_partner;
  utmPartner = typeof partner === 'string' && partner !== '' ? partner : null;
}

export function getLandingUrlParams(): string {
  return landingUrlParams ?? '';
}

export function getUtmPartner(): string | null {
  return utmPartner;
}

/**
 * Attribution properties for `Signup: Completed`, which fires after the wizard
 * has dropped the marketing params from the URL — the landing capture is the
 * only source left. Parsed UTM keys use the casing Marketing's dashboards key
 * on; `URL Params` keeps the raw-string shape the startup application sends,
 * so Marketing's existing parsing works on this event too. Emitted only when
 * attribution is actually present.
 */
export function getLandingAttributionProperties() {
  const params = new URLSearchParams(getLandingUrlParams());
  const properties: Record<string, string> = {};

  for (const param of UTM_PARAMS) {
    const value = params.get(param);
    if (value != null && value !== '') {
      properties[UTM_PARAM_NAMES[param]] = value;
    }
  }

  const attribution = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    if (ATTRIBUTION_PARAM_KEYS.has(key)) {
      attribution.set(key, value);
    }
  }

  const serialized = attribution.toString();
  if (serialized !== '') {
    properties['URL Params'] = `?${serialized}`;
  }

  return properties;
}

/** Test-only: forget any capture from a previous test. */
export function resetLandingUrlParamsForTesting(): void {
  landingUrlParams = null;
  utmPartner = null;
}
